import { ReactElement } from 'react';
import FormGroup from '~/components/form/FormGroup';
import { FormHorizontal } from '~/components/form/layout';
import { useTz } from '~/components/hooks/useTz';
import { InputCardOption } from '~/components/input/InputCards';
import { InputSelectOption } from '~/components/input/InputSelect';
import ZodFieldCheckbox from '~/components/zod/ZodFieldCheckbox';
import ZodFieldDate from '~/components/zod/ZodFieldDate';
import ZodFieldCards from '~/components/zod/ZodFieldCards';
import ZodFieldSelect from '~/components/zod/ZodFieldSelect';
import { useZodFormFieldSingleValue } from '~/components/zod/ZodForm';
import { BreOrderContext } from '~/tenants/bre/model/BreOrderContext';
import { CONTENT_WRITING_RATE, isBusinessDay, plusBusinessDays } from '~/tenants/bre/performable/common';
import {
  BookletSide,
  countSchema,
  paperSchema,
  sideSchema,
} from '~/tenants/bre/performable/print/booklets/BreBookletsConfig';
import { BreBookletsRate } from '~/tenants/bre/performable/print/booklets/BreBookletsData';
import BreBookletsJob from '~/tenants/bre/performable/print/booklets/BreBookletsJob';
import { TenantPerformableFormProps } from '~/tenants/common/form';
import { TenantPackageAddOnCheckboxName } from '~/tenants/common/package';
import { TenantPerformableAddOns } from '~/tenants/common/performable';

const SIDE_OPTS = sideSchema.options.map<InputCardOption>((value) => ({
  title: `${value} Pages`,
  description: 'booklet',
  value,
}));

const COUNT_OPTS = countSchema.options.map<InputCardOption>((value) => ({
  title: `${value} pieces`,
  description: 'print',
  value,
}));

export default function BreBookletsForm({ persona }: TenantPerformableFormProps<BreOrderContext>): ReactElement {
  const tz = useTz();
  const sides = useZodFormFieldSingleValue<BookletSide>('sides') ?? '8';

  const paperOptions = paperSchema.options
    .filter((value) => BreBookletsRate[sides]?.[value])
    .map<InputSelectOption>((value) => ({
      label: BreBookletsJob.pageType({ paper: value }),
      value,
    }));

  return (
    <FormGroup>
      <FormHorizontal name="sides" label="Sides" required>
        <ZodFieldCards name="sides" options={SIDE_OPTS} required />
      </FormHorizontal>
      <FormHorizontal name="count" label="Count" required>
        <ZodFieldCards name="count" options={COUNT_OPTS} cols={2} required />
      </FormHorizontal>
      <FormHorizontal name="paper" label="Paper" required>
        <ZodFieldSelect name="paper" options={paperOptions} />
      </FormHorizontal>
      <FormHorizontal name="no_free_uv" label="UV Cover">
        <label className="flex gap-2 cursor-pointer" htmlFor="no_free_uv">
          <ZodFieldCheckbox id="no_free_uv" name="no_free_uv" />
          Opt-out of free UV cover
        </label>
      </FormHorizontal>
      <FormHorizontal name={'need_by'} label="Need By" required>
        <ZodFieldDate
          name="need_by"
          filter={(date) => isBusinessDay(date) && date >= plusBusinessDays(tz().startOf('day'), 2)}
        />
      </FormHorizontal>

      <TenantPerformableAddOns persona={persona}>
        <TenantPackageAddOnCheckboxName
          name="content_writing"
          title="Content writing"
          description="Add content writing for all of your marketing pieces"
          image="tenant/bre/print/content_writing.jpg"
          persona={persona}
          cost={CONTENT_WRITING_RATE}
        />
      </TenantPerformableAddOns>
    </FormGroup>
  );
}
